<script setup>
/**
 * FormControlMBasketOrderitemsVariationId
 * Displays product variation metadata if a variation exists on the order item.
 * Otherwise, falls back to a numeric field to input a variation ID manually.
 */
import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue';
import FormControlTypeNumber from '../form-control-type/FormControlTypeNumber.vue';

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    },
    value: {
        type: [Number, String],
        default: null
    }
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- State ---

const variationData = ref(null);
const isLoading = ref(false);

// --- Computed ---

const description = computed(() => {
    const rawDescription = props.currentData?.description;

    if (!rawDescription) return null;

    // If it's already an object, just return it
    if (typeof rawDescription === 'object') return rawDescription;

    // If it's a string, safely parse it
    try {
        return JSON.parse(rawDescription);
    } catch (e) {
        console.error("Failed to parse variation description JSON string:", e);
        return null;
    }
});

// --- Methods ---

/**
 * Resolves variation data from the store if both an itemId and variationId are present.
 * Keeps async computed architecture safe for the upcoming Vite shift.
 */
const fetchVariationDetails = async () => {
    const productId = props.currentData?.itemId;
    const variationId = props.value;

    if (!productId || !variationId) {
        variationData.value = null;
        return;
    }

    isLoading.value = true;
    try {
        variationData.value = await store.dispatch('productVariations/getVariation', {
            productId,
            variationId
        });
    } catch (error) {
        console.error('Failed to fetch variation description details:', error);
        variationData.value = null;
    } finally {
        isLoading.value = false;
    }
};

// --- Lifecycle & Watchers ---

onMounted(() => {
    fetchVariationDetails();
});

// Re-run whenever the item configuration or selected variation ID changes
watch([() => props.value, () => props.currentData?.itemId], () => {
    fetchVariationDetails();
});
</script>

<template>
    <div class="variation-resolver-wrapper">
        <FormControlTypeNumber
            v-if="!value"
            ref="input"
            v-bind="$props"
            v-on="$listeners"
        />

        <div v-else-if="description" class="p-2 border rounded bg-light text-dark small">
            <div
                v-for="(val, name, index) in description"
                :key="name"
                :class="{ 'mb-1': index < Object.keys(description).length - 1 }"
            >
                <span class="text-muted fw-semibold" style="text-transform: capitalize;">
                    {{ name }}:
                </span>
                <span class="ms-1 fw-bold">{{ val }}</span>
            </div>
        </div>

        <div v-else class="text-muted small italic p-1">
            <i class="bi bi-info-circle me-1"></i>
            Variation ID #{{ value }} (No descriptions saved)
        </div>
    </div>
</template>

<style scoped>
.variation-resolver-wrapper {
    width: 100%;
}
</style>